import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from './useAuth';

export function useAutoSave(transcriptId, getData, interval = 30000) {
  const { user } = useAuth();
  const intervalRef = useRef();
  const lastSavedDataRef = useRef(null);

  const performAutoSave = useCallback(async () => {
    if (!user || !transcriptId) return;

    try {
      // Use requestIdleCallback or setTimeout to avoid blocking main thread
      const runSave = async () => {
        const currentData = await getData();
        if (!currentData) return;

        // Skip if data hasn't changed
        const currentDataString = JSON.stringify(currentData);
        if (lastSavedDataRef.current === currentDataString) {
          return;
        }

        const token = localStorage.getItem('authToken');
        if (!token) return;

        const response = await fetch(`/api/transcripts/${transcriptId}/auto-save`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ data: currentData })
        });

        if (response.ok) {
          lastSavedDataRef.current = currentDataString;
          console.log('Auto-saved successfully');
        }
      };

      // Use requestIdleCallback if available, otherwise use setTimeout
      if (typeof requestIdleCallback !== 'undefined') {
        requestIdleCallback(() => runSave(), { timeout: 2000 });
      } else {
        setTimeout(() => runSave(), 0);
      }
    } catch (error) {
      console.error('Auto-save failed:', error);
    }
  }, [transcriptId, getData, user]);

  const startAutoSave = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(performAutoSave, interval);
  }, [performAutoSave, interval]);

  const stopAutoSave = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const saveNow = useCallback(async () => {
    await performAutoSave();
  }, [performAutoSave]);

  useEffect(() => {
    if (user && transcriptId) {
      startAutoSave();
    } else {
      stopAutoSave();
    }

    return () => {
      stopAutoSave();
    };
  }, [user, transcriptId, startAutoSave, stopAutoSave]);

  return {
    startAutoSave,
    stopAutoSave,
    saveNow
  };
}